import React, { useState, useEffect } from "react";
import {
  useGetAccountQuery,
  useUpdatePreferencesMutation,
} from "../../../api/account";
import AccountSectionEditInPlaceWrapper from "./AccountSectionEditInPlaceWrapper";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import ApiResponseModal from "../popup/ApiResponseModal";
import MultiSelect from "../forms/MultiSelect";
import { getSelectOptions } from "../../../utils/selectHelpers";

const ExpertiseContent = () => {
  // Removed modal states since we're using edit-in-place mode
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // API hooks
  const { data: accountInfo, isSuccess: isAccountLoaded } =
    useGetAccountQuery();
  const [updatePreferences] = useUpdatePreferencesMutation();

  // Edit mode states for each section
  const [isPreferredClientEditMode, setIsPreferredClientEditMode] =
    useState(false);
  const [isFileForStatesEditMode, setIsFileForStatesEditMode] = useState(false);
  const [isLanguagesEditMode, setIsLanguagesEditMode] = useState(false);

  // Checkbox states for Preferred Client
  const [preferredClientSelection, setPreferredClientSelection] = useState({
    individual: false,
    business: false,
    both: true,
  });

  // Track original state to detect changes
  const [
    originalPreferredClientSelection,
    setOriginalPreferredClientSelection,
  ] = useState({
    individual: false,
    business: false,
    both: true,
  });

  // Track if we've already initialized from API
  const [hasInitialized, setHasInitialized] = useState(false);

  // Modal states for preferences update
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferencesModalMessage, setPreferencesModalMessage] = useState("");
  const [isPreferencesSuccess, setIsPreferencesSuccess] = useState(false);
  const [isPreferencesLoading, setIsPreferencesLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentPreferencesData, setCurrentPreferencesData] = useState(null);

  // Initialize state from API data (only once)
  useEffect(() => {
    if (
      isAccountLoaded &&
      accountInfo?.preferences?.skills_expertise &&
      !hasInitialized
    ) {
      const skillsData = accountInfo.preferences.skills_expertise;

      // Initialize preferred filing type
      if (skillsData.preferred_filing_type) {
        const filingTypes = skillsData.preferred_filing_type;
        const newSelection = {
          individual: filingTypes.includes("individual"),
          business: filingTypes.includes("business"),
          both: filingTypes.includes("both"),
        };
        setPreferredClientSelection(newSelection);
        setOriginalPreferredClientSelection(newSelection);
      }

      // Initialize states
      if (skillsData.preferred_states) {
        // If all_states is present, we should only show "All States" and clear specific states
        const isAllStates = skillsData.preferred_states.includes("all_states");
        const filteredStates = skillsData.preferred_states.filter(
          (state) => state !== "all_states"
        );

        const newStatesData = {
          selectedStates: isAllStates ? [] : filteredStates, // If all_states, show empty array (will display "All States")
          isAllSelected: isAllStates,
        };
        setFileForStatesData(newStatesData);
        setOriginalFileForStatesData(newStatesData);
      }

      // Initialize languages
      if (skillsData.languages) {
        const newLanguagesData = {
          selectedLanguages: skillsData.languages,
          isEnglishSelected: skillsData.languages.includes("english"),
        };
        setLanguagesData(newLanguagesData);
        setOriginalLanguagesData(newLanguagesData);
      }

      setHasInitialized(true);
    }
  }, [isAccountLoaded, accountInfo, hasInitialized]);

  // File For States state
  const [fileForStatesData, setFileForStatesData] = useState({
    selectedStates: [],
    isAllSelected: true,
  });

  // Check if changes have been made
  const hasPreferredClientChanges =
    JSON.stringify(preferredClientSelection) !==
    JSON.stringify(originalPreferredClientSelection);

  // Track original state for File For States
  const [originalFileForStatesData, setOriginalFileForStatesData] = useState({
    selectedStates: [],
    isAllSelected: true,
  });

  // Get US states from centralized selectOptions
  // Map to use label as value to match current storage format (full state names)
  const allStateOptions = getSelectOptions("usaStates").map((option) => ({
    value: option.value, // Use label (full name) as value to match stored format
    label: option.label,
  }));

  // Get available states (not yet selected)
  const availableStates = allStateOptions.filter(
    (option) => !fileForStatesData.selectedStates.includes(option.value)
  );

  // Check if File For States changes have been made
  const hasFileForStatesChanges =
    JSON.stringify(fileForStatesData) !==
    JSON.stringify(originalFileForStatesData);

  // Language(s) state
  const [languagesData, setLanguagesData] = useState({
    selectedLanguages: ["English"],
  });

  // Track original state for Language(s)
  const [originalLanguagesData, setOriginalLanguagesData] = useState({
    selectedLanguages: ["English"],
  });

  // Available languages
  const availableLanguages = [
    "English",
    "Spanish",
    "French",
    "Russian",
    "Chinese",
  ];

  // Get available languages (not yet selected)
  const availableLanguagesForSelect = availableLanguages.filter(
    (lang) => !languagesData.selectedLanguages.includes(lang)
  );

  // Check if Language(s) changes have been made
  const hasLanguagesChanges =
    JSON.stringify(languagesData) !== JSON.stringify(originalLanguagesData);

  // Handle adding a state
  const handleAddState = (state) => {
    if (state === "All") {
      setFileForStatesData((prev) => ({
        ...prev,
        selectedStates: [],
        isAllSelected: true,
      }));
    } else {
      setFileForStatesData((prev) => ({
        ...prev,
        selectedStates: [...prev.selectedStates, state],
        isAllSelected: false,
      }));
    }
  };

  // Handle removing a state
  const handleRemoveState = (state) => {
    setFileForStatesData((prev) => ({
      ...prev,
      selectedStates: prev.selectedStates.filter((s) => s !== state),
      isAllSelected: false, // Never auto-set to All when removing states
    }));
  };

  // Handle adding a language
  const handleAddLanguage = (language) => {
    setLanguagesData((prev) => ({
      ...prev,
      selectedLanguages: [...prev.selectedLanguages, language],
    }));
  };

  // Handle removing a language (except English)
  const handleRemoveLanguage = (language) => {
    if (language === "English") return; // English cannot be removed
    setLanguagesData((prev) => ({
      ...prev,
      selectedLanguages: prev.selectedLanguages.filter(
        (lang) => lang !== language
      ),
    }));
  };

  // Handle checkbox changes with automatic "Both" logic
  const handlePreferredClientChange = (field, checked) => {
    setPreferredClientSelection((prev) => {
      const newSelection = { ...prev, [field]: checked };

      // If "Both" is checked, uncheck Individual and Business
      if (field === "both" && checked) {
        newSelection.individual = false;
        newSelection.business = false;
      }
      // If Individual and Business are both checked, check "Both" and uncheck them
      else if (field === "individual" && checked && prev.business) {
        newSelection.both = true;
        newSelection.individual = false;
        newSelection.business = false;
      } else if (field === "business" && checked && prev.individual) {
        newSelection.both = true;
        newSelection.individual = false;
        newSelection.business = false;
      }
      // If only one is checked, uncheck "Both"
      else if (
        (field === "individual" && checked && !prev.business) ||
        (field === "business" && checked && !prev.individual)
      ) {
        newSelection.both = false;
      }
      // If unchecking Individual or Business, and "Both" was checked, uncheck "Both"
      else if (
        (field === "individual" && !checked) ||
        (field === "business" && !checked)
      ) {
        newSelection.both = false;
      }

      return newSelection;
    });
  };

  // Modal handlers
  const handlePreferencesUpdate = async (section, preferencesData) => {
    setCurrentSection(section);
    setCurrentPreferencesData(preferencesData);
    setShowPreferencesModal(true);
    setIsPreferencesLoading(true);
    setIsPreferencesSuccess(false);

    try {
      const result = await updatePreferences({
        section: section,
        preferences: preferencesData,
      }).unwrap();

      setIsPreferencesLoading(false);
      setPreferencesModalMessage(
        "Your information has been updated successfully."
      );
      setIsPreferencesSuccess(true);
    } catch (error) {
      setIsPreferencesLoading(false);
      setPreferencesModalMessage(
        "We couldn’t update your information. Please try again."
      );
      setIsPreferencesSuccess(false);
    }
  };

  const handleClosePreferencesModal = () => {
    setShowPreferencesModal(false);
    if (isPreferencesSuccess) {
      // Reset original state to current state after successful save and exit edit mode
      if (currentSection === "skills_expertise") {
        // Update the appropriate original state and exit edit mode
        setOriginalPreferredClientSelection(preferredClientSelection);
        setOriginalFileForStatesData(fileForStatesData);
        setOriginalLanguagesData(languagesData);
        setIsPreferredClientEditMode(false);
        setIsFileForStatesEditMode(false);
        setIsLanguagesEditMode(false);
      }
    }
  };

  const handleRetryPreferences = () => {
    if (currentSection && currentPreferencesData) {
      handlePreferencesUpdate(currentSection, currentPreferencesData);
    }
  };

  // Simulate brief loading for consistency and future API calls
  useEffect(() => {
    const timer = setTimeout(() => setIsInitialLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (isInitialLoading) {
    return <LoadingPlaceholder rows={2} type="card-large" />;
  }

  return (
    <>
      <AccountSectionEditInPlaceWrapper
        title="Preferred Filing Type"
        isEditMode={isPreferredClientEditMode}
        onToggleEdit={() =>
          setIsPreferredClientEditMode(!isPreferredClientEditMode)
        }
        hasChanges={hasPreferredClientChanges}
        onApplyChanges={() => {
          // Call the modal handler with the preferences data
          handlePreferencesUpdate("skills_expertise", {
            preferred_filing_type: Object.keys(preferredClientSelection).filter(
              (key) => preferredClientSelection[key]
            ),
          });
        }}
        onDiscardChanges={() => {
          setPreferredClientSelection(originalPreferredClientSelection);
        }}
        infoTooltip="Select the types of filings you usually handle. Smart Match will prioritize your preferences whenever possible."
        section="skills_expertise"
        preferencesData={{
          preferred_filing_type: Object.keys(preferredClientSelection).filter(
            (key) => preferredClientSelection[key]
          ),
        }}
      >
        <div className="form-field">
          <div className="checkbox-container">
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="individual"
                  checked={preferredClientSelection.individual}
                  onChange={(e) =>
                    handlePreferredClientChange("individual", e.target.checked)
                  }
                  disabled={!isPreferredClientEditMode}
                />
                <label htmlFor="individual">Individual</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="business"
                  checked={preferredClientSelection.business}
                  onChange={(e) =>
                    handlePreferredClientChange("business", e.target.checked)
                  }
                  disabled={!isPreferredClientEditMode}
                />
                <label htmlFor="business">Business</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="both"
                  checked={preferredClientSelection.both}
                  onChange={(e) =>
                    handlePreferredClientChange("both", e.target.checked)
                  }
                  disabled={!isPreferredClientEditMode}
                />
                <label htmlFor="both">Both</label>
              </div>
            </div>
          </div>
        </div>
      </AccountSectionEditInPlaceWrapper>

      <AccountSectionEditInPlaceWrapper
        title="Preferred States for Tax Filings"
        isEditMode={isFileForStatesEditMode}
        onToggleEdit={() =>
          setIsFileForStatesEditMode(!isFileForStatesEditMode)
        }
        hasChanges={hasFileForStatesChanges}
        onApplyChanges={() => {
          // Ensure we don't mix all_states with specific states
          const statesToSend = fileForStatesData.isAllSelected
            ? ["all_states"]
            : fileForStatesData.selectedStates.filter(
                (state) => state !== "all_states"
              );

          handlePreferencesUpdate("skills_expertise", {
            preferred_states: statesToSend,
          });
        }}
        onDiscardChanges={() => {
          setFileForStatesData(originalFileForStatesData);
        }}
        infoTooltip="Choose the states you’re most comfortable working with. Smart Match will try to align matches with your preferred regions whenever possible."
        section="skills_expertise"
        preferencesData={{
          preferred_states: fileForStatesData.isAllSelected
            ? ["all_states"]
            : fileForStatesData.selectedStates,
        }}
      >
        <div className="form-field">
          <MultiSelect
            options={[
              { value: "All", label: "All States" },
              ...availableStates,
            ]}
            selectedValues={fileForStatesData.selectedStates}
            allSelected={fileForStatesData.isAllSelected}
            allSelectedValue="All"
            onAdd={handleAddState}
            onRemove={handleRemoveState}
            onToggleAll={() =>
              setFileForStatesData((prev) => ({
                ...prev,
                isAllSelected: !prev.isAllSelected,
                selectedStates: prev.isAllSelected ? [] : [],
              }))
            }
            placeholder="Select a state to add..."
            disabled={!isFileForStatesEditMode}
            renderAllChip={() => (
              <div className="selected-chip all-chip">
                <span className="chip-text">All States</span>
                {isFileForStatesEditMode && (
                  <button
                    type="button"
                    className="remove-chip-btn"
                    onClick={() =>
                      setFileForStatesData((prev) => ({
                        ...prev,
                        isAllSelected: false,
                        selectedStates: [],
                      }))
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            renderChip={(state) => (
              <div key={state} className="selected-chip">
                <span className="chip-text">{state}</span>
                {isFileForStatesEditMode && (
                  <button
                    type="button"
                    className="remove-chip-btn"
                    onClick={() => handleRemoveState(state)}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          />
        </div>
      </AccountSectionEditInPlaceWrapper>

      <AccountSectionEditInPlaceWrapper
        title="Languages You Work In"
        isEditMode={isLanguagesEditMode}
        onToggleEdit={() => setIsLanguagesEditMode(!isLanguagesEditMode)}
        hasChanges={hasLanguagesChanges}
        onApplyChanges={() => {
          // Call the modal handler with the preferences data
          handlePreferencesUpdate("skills_expertise", {
            languages: languagesData.selectedLanguages,
          });
        }}
        onDiscardChanges={() => {
          setLanguagesData(originalLanguagesData);
        }}
        infoTooltip="Choose your working languages. Smart Match considers these when assigning new clients."
        section="skills_expertise"
        preferencesData={{
          languages: languagesData.selectedLanguages,
        }}
      >
        <div className="form-field">
          <MultiSelect
            options={availableLanguagesForSelect.map((language) => ({
              value: language,
              label: language,
            }))}
            selectedValues={languagesData.selectedLanguages}
            onAdd={handleAddLanguage}
            onRemove={handleRemoveLanguage}
            placeholder="Select a language to add..."
            disabled={!isLanguagesEditMode}
            requiredValue="English"
            renderChip={(language) => (
              <div
                key={language}
                className={`selected-chip ${
                  language === "English" ? "required-chip" : ""
                }`}
              >
                <span className="chip-text">{language}</span>
                {isLanguagesEditMode && language !== "English" && (
                  <button
                    type="button"
                    className="remove-chip-btn"
                    onClick={() => handleRemoveLanguage(language)}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          />
        </div>
      </AccountSectionEditInPlaceWrapper>

      {/* Preferences Update Modal */}
      <ApiResponseModal
        isOpen={showPreferencesModal}
        isLoading={isPreferencesLoading}
        isSuccess={isPreferencesSuccess}
        message={preferencesModalMessage}
        onClose={handleClosePreferencesModal}
        onRetry={handleRetryPreferences}
      />
    </>
  );
};

export default ExpertiseContent;
